import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Minus, Plus, ShoppingBag, Share2, Lock, Check, Clock, Ruler, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useCart, money, sortSizes } from "@/context/CartContext";
import SEO from "@/components/SEO";
import Lightbox from "@/components/events/Lightbox";
import { isNewIn } from "@/components/shop/ShopSpotlight";
import { splitProductCopy, parseShippingTimes } from "@/components/shop/productCopy";
import { tryNativeShare, copyShareBlob } from "@/lib/share";

const SITE = "https://abnormalworld.it";

export default function ProductDetail() {
    const { handle } = useParams();
    const { lang } = useLang();
    const en = lang === "en";
    const { add, busy } = useCart();
    const [p, setP] = useState(null);
    const [missing, setMissing] = useState(false);
    const [sel, setSel] = useState({});
    const [qty, setQty] = useState(1);
    const [img, setImg] = useState(0);
    const [times, setTimes] = useState(null);
    const [zoom, setZoom] = useState(null);

    useEffect(() => {
        setP(null); setMissing(false); setQty(1); setImg(0);
        window.scrollTo(0, 0);
        api.get(`/shop/catalog/${handle}`).then((r) => {
            setP(r.data);
            const first = r.data.variants.find((v) => v.availableForSale) || r.data.variants[0];
            setSel(Object.fromEntries((first?.selectedOptions || []).map((o) => [o.name, o.value])));
        }).catch(() => setMissing(true));
    }, [handle]);

    useEffect(() => {
        api.get("/shop/policies").then((r) => setTimes(parseShippingTimes(r.data?.shipping?.body))).catch(() => setTimes(null));
    }, []);

    const variant = useMemo(() => p?.variants.find((v) => v.selectedOptions.every((o) => sel[o.name] === o.value)) || null, [p, sel]);
    const options = useMemo(() => (p?.options || [])
        .filter((o) => !(o.values.length === 1 && o.values[0] === "Default Title"))
        .map((o) => (/size|taglia/i.test(o.name) ? { ...o, values: sortSizes(o.values) } : o)), [p]);
    const availableFor = (name, value) => p.variants.some((v) => v.availableForSale && v.selectedOptions.every((o) => o.name === name ? o.value === value : (sel[o.name] === o.value || false)));

    useEffect(() => {
        if (!variant?.image?.url || !p) return;
        const i = p.images.findIndex((x) => x.url === variant.image.url);
        if (i >= 0) setImg(i);
    }, [variant, p]);

    if (missing) {
        return (
            <div data-testid="product-not-found" className="bg-abnormal-ink min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5 text-center">
                <p className="font-display uppercase text-5xl text-abnormal-paper">404<span className="text-abnormal-acid">.</span></p>
                <p className="text-abnormal-paperDim">{en ? "Product not found." : "Prodotto non trovato."}</p>
                <Link to="/shop" className="btn-ghost"><ArrowLeft size={14} /> Shop</Link>
            </div>
        );
    }
    if (!p) return <div className="bg-abnormal-ink min-h-[60vh]" />;

    const images = p.images?.length ? p.images : (p.featuredImage ? [p.featuredImage] : []);
    const price = variant?.price || p.priceRange?.minVariantPrice;
    const cmp = variant?.compareAtPrice;
    const onSale = cmp && Number(cmp.amount) > Number(price?.amount || 0);
    const canBuy = variant && variant.availableForSale;
    const url = `${SITE}/shop/${p.handle}`;
    const copy = splitProductCopy(p.descriptionHtml || "", lang);
    const sizeOption = options.find((o) => /size|taglia/i.test(o.name));
    const noticeHtml = DOMPurify.sanitize(copy.notice, { ADD_ATTR: ["target"] });
    const seoDesc = copy.details.slice(0, 3).join(" · ") || (en ? `${p.title} — ABnormal Clothing, made to order.` : `${p.title} — ABnormal Clothing, realizzata su ordinazione.`);

    const share = async () => {
        const text = `${p.title.toUpperCase()} · ${money(price, lang)}\n— ABnormal`;
        const r = await tryNativeShare({ title: p.title, text, url });
        if (!r.used && await copyShareBlob(text, url)) toast.success(en ? "Link copied" : "Link copiato");
    };
    const addToCart = () => {
        if (!variant) return;
        const label = variant.selectedOptions.filter((o) => o.value !== "Default Title").map((o) => o.value).join(" / ");
        add(variant.id, qty, `${p.title}${label ? ` · ${label}` : ""} ${en ? "added" : "aggiunto"}`);
    };
    const jsonLd = {
        "@context": "https://schema.org", "@type": "Product", name: p.title, image: images.map((i) => i.url), description: seoDesc, url,
        brand: { "@type": "Brand", name: p.vendor || "ABnormal" },
        offers: { "@type": "AggregateOffer", priceCurrency: price?.currencyCode || "EUR", lowPrice: p.priceRange?.minVariantPrice?.amount, highPrice: p.priceRange?.maxVariantPrice?.amount, availability: p.availableForSale ? "https://schema.org/InStock" : "https://schema.org/OutOfStock", url },
    };

    return (
        <div data-testid="product-page" className="bg-abnormal-ink">
            <SEO title={`${p.title} · Shop ABnormal`} description={seoDesc.slice(0, 160)} image={images[0]?.url} path={`/shop/${p.handle}`} type="product" />
            <Helmet><script type="application/ld+json">{JSON.stringify(jsonLd)}</script></Helmet>

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-6 md:pt-10">
                <Link to="/shop" data-testid="product-back" className="label-mono text-abnormal-paperDim hover:text-abnormal-acid inline-flex items-center gap-1.5"><ArrowLeft size={12} /> Shop</Link>
            </div>

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-6 pb-16 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                {/* Gallery */}
                <div className="lg:col-span-7">
                    <button type="button" onClick={() => setZoom(images[img]?.url)} data-testid="product-main-image" className="block w-full aspect-square bg-black border border-white/15 overflow-hidden cursor-zoom-in">
                        {images[img] && <img src={images[img].url} alt={images[img].altText || p.title} className="w-full h-full object-cover" />}
                    </button>
                    {images.length > 1 && (
                        <div data-testid="product-thumbs" className="mt-3 grid grid-cols-5 sm:grid-cols-6 gap-2">
                            {images.map((im, i) => (
                                <button key={im.url} type="button" onClick={() => setImg(i)} data-testid={`product-thumb-${i}`} aria-label={`Immagine ${i + 1}`}
                                    className={`aspect-square bg-black border overflow-hidden ${i === img ? "border-abnormal-acid" : "border-white/10 hover:border-white/40"}`}>
                                    <img src={im.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Buy box */}
                <div className="lg:col-span-5 lg:sticky lg:top-24">
                    <p className="label-mono text-abnormal-acid">{p.productType || p.vendor || "ABNORMAL"}{onSale && <span className="ml-2 bg-abnormal-magenta text-black px-1.5 py-0.5">SALE</span>}{isNewIn(p) && <span data-testid="new-in-detail" className="ml-2 bg-abnormal-acid text-black px-1.5 py-0.5">NEW IN</span>}</p>
                    <h1 data-testid="product-title" className="mt-2 font-display uppercase leading-[0.85] tracking-tighter text-abnormal-paper text-4xl sm:text-5xl">{p.title}<span className="text-abnormal-acid">.</span></h1>
                    <p className="mt-4 flex items-baseline gap-3">
                        <span data-testid="product-price" className="font-display text-4xl text-abnormal-paper">{money(price, lang)}</span>
                        {onSale && <span className="font-display text-2xl text-abnormal-paperDim line-through">{money(cmp, lang)}</span>}
                        <span className="label-mono text-abnormal-paperDim/60">{en ? "VAT incl." : "IVA incl."}</span>
                    </p>

                    {options.map((o) => (
                        <div key={o.name} className="mt-6" data-testid={`product-option-${o.name.toLowerCase()}`}>
                            <p className="label-mono text-abnormal-paperDim mb-2">{en ? o.name : ({ Color: "Colore", Size: "Taglia", Colour: "Colore" }[o.name] || o.name)} <span className="text-abnormal-paper">· {sel[o.name]}</span></p>
                            <div className="flex flex-wrap gap-2">
                                {o.values.map((v) => {
                                    const active = sel[o.name] === v;
                                    const avail = availableFor(o.name, v);
                                    const isColor = /colou?r|colore/i.test(o.name);
                                    return (
                                        <button key={v} type="button" onClick={() => setSel((s) => ({ ...s, [o.name]: v }))} data-testid={`product-opt-${o.name.toLowerCase()}-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            className={`min-w-[44px] h-11 px-3 border font-vt text-xs tracking-[0.15em] uppercase transition-colors ${active ? "bg-abnormal-acid text-black border-abnormal-acid" : avail ? "border-white/20 text-abnormal-paper hover:border-abnormal-acid" : "border-white/10 text-abnormal-paperDim/50 line-through"} ${isColor ? "rounded-none" : ""}`}>
                                            {v}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    <div className="mt-6 flex items-center gap-3">
                        <div className="inline-flex items-center border border-white/20 h-12">
                            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="product-qty-minus" className="w-11 h-full inline-flex items-center justify-center hover:text-abnormal-acid"><Minus size={14} /></button>
                            <span data-testid="product-qty" className="w-10 text-center font-vt">{qty}</span>
                            <button type="button" onClick={() => setQty((q) => Math.min(10, q + 1))} data-testid="product-qty-plus" className="w-11 h-full inline-flex items-center justify-center hover:text-abnormal-acid"><Plus size={14} /></button>
                        </div>
                        {p.channel === "online_store" ? (
                            <a href={variant?.buyUrl || p.buyUrl} target="_blank" rel="noopener noreferrer" data-testid="product-buy-on-shopify"
                                aria-disabled={!canBuy}
                                className={`flex-1 h-12 inline-flex items-center justify-center gap-2 bg-abnormal-acid text-black hover:bg-abnormal-paper text-xs uppercase tracking-[0.2em] font-bold transition-colors ${!canBuy ? "opacity-40 pointer-events-none" : ""}`}>
                                <ExternalLink size={15} /> {canBuy ? (en ? "Buy on Shopify" : "Acquista su Shopify") : (en ? "Sold out" : "Esaurito")}
                            </a>
                        ) : (
                        <button type="button" onClick={addToCart} disabled={!canBuy || busy} data-testid="product-add-to-cart"
                            className="flex-1 h-12 inline-flex items-center justify-center gap-2 bg-abnormal-acid text-black hover:bg-abnormal-paper disabled:opacity-40 disabled:cursor-not-allowed text-xs uppercase tracking-[0.2em] font-bold transition-colors">
                            <ShoppingBag size={15} /> {canBuy ? (en ? "Add to cart" : "Aggiungi al carrello") : (en ? "Sold out" : "Esaurito")}
                        </button>
                        )}
                        <button type="button" onClick={share} data-testid="product-share" aria-label="Condividi" className="w-12 h-12 inline-flex items-center justify-center border border-white/20 hover:border-abnormal-acid hover:text-abnormal-acid"><Share2 size={15} /></button>
                    </div>
                    {variant?.sku && <p className="mt-2 label-mono text-abnormal-paperDim/50">SKU {variant.sku}</p>}
                    <div data-testid="product-delivery" className="mt-4 border border-white/15 bg-abnormal-charcoal px-4 py-3 text-sm leading-snug">
                        <p className="label-mono text-abnormal-acid mb-1 inline-flex items-center gap-2"><Clock size={12} /> {en ? "MADE TO ORDER" : "REALIZZATA SU ORDINAZIONE"}</p>
                        {times ? (
                            <p className="text-abnormal-paper">
                                {en
                                    ? <>Production <b>{times.production} working days</b>{times.italy && <>, then delivery Italy <b>{times.italy} working days</b></>}{times.europe && <> · Europe <b>{times.europe}</b></>}. Indicative, not guaranteed dates.</>
                                    : <>Produzione <b>{times.production} giorni lavorativi</b>{times.italy && <>, poi consegna Italia <b>{times.italy} giorni lavorativi</b></>}{times.europe && <> · Europa <b>{times.europe}</b></>}. Tempi indicativi, non date garantite.</>}
                            </p>
                        ) : (
                            <p className="text-abnormal-paperDim">{en ? "Printed after your order and shipped by Printful; see shipping times." : "Stampata dopo l'ordine e spedita da Printful; vedi i tempi di spedizione."}</p>
                        )}
                        <Link to="/shop/info?tab=shipping" data-testid="product-delivery-link" className="label-mono text-abnormal-paperDim hover:text-abnormal-acid">{en ? "Shipping policy →" : "Politica di spedizione →"}</Link>
                    </div>
                    <details data-testid="product-size-guide" className="mt-3 border border-white/10 group">
                        <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between label-mono text-abnormal-paper hover:text-abnormal-acid transition-colors">
                            <span className="inline-flex items-center gap-2"><Ruler size={12} /> {en ? "SIZE GUIDE" : "GUIDA TAGLIE"}</span>
                            <Plus size={13} className="group-open:rotate-45 transition-transform" />
                        </summary>
                        <div className="px-4 pb-4 text-sm text-abnormal-paperDim leading-snug space-y-2">
                            {sizeOption && <p>{en ? "Available sizes:" : "Taglie disponibili:"} <b className="text-abnormal-paper">{sizeOption.values.join(" · ")}</b></p>}
                            {copy.fitNotes.map((n, i) => <p key={i} className="border-l-2 border-abnormal-acid pl-3">{n}</p>)}
                            {copy.sizeTable ? (
                                <div className="overflow-x-auto [&_table]:w-full [&_td,&_th]:border [&_td,&_th]:border-white/10 [&_td,&_th]:px-2 [&_td,&_th]:py-1 [&_th]:text-abnormal-paper" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(copy.sizeTable) }} />
                            ) : (
                                <p data-testid="product-size-missing" className="text-abnormal-paperDim/70">{en ? "Garment measurements in cm are not published for this item yet. Sizes follow the European standard of the printed blank." : "Le misure del capo in cm non sono ancora pubblicate per questo articolo. Le taglie seguono lo standard europeo del capo stampato."}</p>
                            )}
                        </div>
                    </details>
                    <ul className="mt-6 space-y-1.5 label-mono text-abnormal-paperDim">
                        <li className="inline-flex items-center gap-2"><Lock size={11} className="text-abnormal-acid" /> {en ? "Secure Shopify checkout" : "Checkout sicuro Shopify"}</li><br />
                        <li className="inline-flex items-center gap-2"><Check size={11} className="text-abnormal-acid" /> {en ? "Printed on demand & shipped by Printful" : "Stampato su richiesta e spedito da Printful"}</li>
                    </ul>

                    {copy.details.length > 0 && (
                        <div data-testid="product-description" className="mt-8 border-t border-white/10 pt-6">
                            <p className="label-mono text-abnormal-paperDim/60 mb-3">{en ? "// PRODUCT DETAILS" : "// DETTAGLI PRODOTTO"}</p>
                            <ul className="space-y-1.5 text-sm text-abnormal-paperDim leading-snug">
                                {copy.details.map((d, i) => (
                                    <li key={i} className="flex gap-2"><span className="text-abnormal-acid shrink-0">•</span><span>{d}</span></li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {noticeHtml && (
                        <details data-testid="product-shipping-notice" className="mt-6 border border-white/10 group">
                            <summary className="cursor-pointer list-none px-4 py-3 flex items-center justify-between label-mono text-abnormal-paper hover:text-abnormal-acid transition-colors">
                                {en ? "MADE TO ORDER · PRODUCTION & SHIPPING" : "MADE TO ORDER · PRODUZIONE E SPEDIZIONE"}
                                <Plus size={13} className="group-open:rotate-45 transition-transform" />
                            </summary>
                            <div className="px-4 pb-4 prose prose-invert prose-sm max-w-none text-abnormal-paperDim [&_a]:text-abnormal-acid" dangerouslySetInnerHTML={{ __html: noticeHtml }} />
                        </details>
                    )}
                    <p className="mt-4 label-mono text-abnormal-paperDim/50">
                        <Link to="/shop/info" data-testid="product-policies-link" className="hover:text-abnormal-acid">{en ? "Shipping · Returns · Terms →" : "Spedizioni · Resi · Condizioni di vendita →"}</Link>
                    </p>
                </div>
            </div>
            <Lightbox src={zoom} alt={p.title} onClose={() => setZoom(null)} testid="product-lightbox" />
        </div>
    );
}
